import { useEffect, useState } from "react";
import { Download, X, Smartphone, Loader2 } from "lucide-react";

/**
 * V25 — In-Dashboard Tutor PWA Install Card.
 *
 * Rules (per spec — Batch A item #1):
 *   - Renders ONLY when this hook is given an `active` + `approved` tutor.
 *   - Shows the native install prompt when `beforeinstallprompt` fires.
 *   - Hides automatically once the app is installed (display-mode: standalone).
 *   - Per-session dismissable.
 *
 * Public site mounts are intentionally removed — public users (parents) never
 * see the Tutor PWA install option.
 */
const SESSION_KEY = "at_tutor_pwa_install_dismissed_v2";

export default function TutorPwaInstallCard({ tutor }) {
    const [installEvent, setInstallEvent] = useState(null);
    const [installing, setInstalling] = useState(false);
    const [installed, setInstalled] = useState(
        typeof window !== "undefined" && window.matchMedia?.("(display-mode: standalone)").matches
    );
    const [dismissed, setDismissed] = useState(
        typeof window !== "undefined" && sessionStorage.getItem(SESSION_KEY) === "1"
    );

    useEffect(() => {
        const onPrompt = (e) => { e.preventDefault(); setInstallEvent(e); };
        const onInstalled = () => { setInstalled(true); setInstallEvent(null); };
        window.addEventListener("beforeinstallprompt", onPrompt);
        window.addEventListener("appinstalled", onInstalled);
        return () => {
            window.removeEventListener("beforeinstallprompt", onPrompt);
            window.removeEventListener("appinstalled", onInstalled);
        };
    }, []);

    // Strict gate — never render for non-active tutors
    if (!tutor) return null;
    if (tutor.status !== "active") return null;
    if (installed) return null;
    if (dismissed) return null;
    if (!installEvent) return null;  // browser doesn't offer install (e.g. iOS Safari)

    const dismiss = () => {
        try { sessionStorage.setItem(SESSION_KEY, "1"); } catch { /* noop */ }
        setDismissed(true);
    };

    const install = async () => {
        if (!installEvent) return;
        setInstalling(true);
        try {
            await installEvent.prompt();
            await installEvent.userChoice;
            setInstallEvent(null);
        } finally { setInstalling(false); }
    };

    return (
        <section className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white rounded-2xl shadow-md p-4 mb-4 relative overflow-hidden"
            data-testid="tutor-pwa-install-card">
            <div className="absolute -top-8 -right-8 w-24 h-24 bg-white/10 rounded-full"></div>
            <div className="flex items-start gap-3 relative">
                <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center flex-shrink-0">
                    <Smartphone className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.15em] opacity-80 font-bold">PWA</div>
                    <div className="font-bold text-base leading-tight">Install Tutor App</div>
                    <p className="text-[11px] opacity-90 mt-0.5">Faster access · offline support · push alerts</p>
                </div>
                <button onClick={dismiss} data-testid="tutor-pwa-install-dismiss"
                    className="text-white/60 hover:text-white p-1 -mr-1 -mt-1">
                    <X className="w-4 h-4" />
                </button>
            </div>
            <button onClick={install} disabled={installing} data-testid="tutor-pwa-install-btn"
                className="w-full mt-3 bg-white text-emerald-700 font-bold text-sm py-2.5 rounded-xl inline-flex items-center justify-center gap-1.5 active:scale-95 transition disabled:opacity-60">
                {installing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Install now
            </button>
        </section>
    );
}
